import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdCancel, MdContentCopy, MdNotificationsActive, MdSearch, MdVisibility, MdErrorOutline } from 'react-icons/md';
import { getData, putData } from '../../../Webservice/Webservice';
import { FONT, authToken, fmtDateTime } from '../Email/emailShared';
import PushForm, { PushPreview } from './PushForm';
import PushDelivery from './PushDelivery';

const STATUS = {
  pending: { label: 'Starting', bg: '#fef3c7', fg: '#92400e' },
  scheduled: { label: 'Scheduled', bg: '#dbeafe', fg: '#1d4ed8' },
  running: { label: 'Sending', bg: '#dcfce7', fg: '#166534' },
  completed: { label: 'Sent', bg: '#d1fae5', fg: '#065f46' },
  failed: { label: 'Failed', bg: '#fee2e2', fg: '#991b1b' },
  cancelled: { label: 'Cancelled', bg: '#f1f5f9', fg: '#475569' },
};
const StatusChip = ({ status }) => {
  const s = STATUS[status] || { label: status, bg: '#f1f5f9', fg: '#475569' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: s.bg, color: s.fg }} />;
};

const Kpi = ({ title, value, from, to, onClick, active }) => (
  <Box onClick={onClick} sx={{ cursor: onClick ? 'pointer' : 'default', color: '#fff', p: 2, borderRadius: '14px', background: `linear-gradient(135deg,${from},${to})`, outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, opacity: 0.9 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>{Number(value || 0).toLocaleString('en-IN')}</Typography>
  </Box>
);

const audienceText = (a = {}) => {
  if (a.type === 'selected') return `${(a.customerIds || []).length} chosen customer${(a.customerIds || []).length === 1 ? '' : 's'}`;
  const phones = (a.platforms || []).length === 1 ? (a.platforms[0] === 'ios' ? 'iPhone users' : 'Android users') : 'All app users';
  return (a.cities || []).length ? `${phones} in ${a.cities.join(', ')}` : phones;
};

function PushNotification() {
  const [view, setView] = useState('list'); // list | form
  const [source, setSource] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [firebase, setFirebase] = useState({ ok: true });
  const [meta, setMeta] = useState({ total: 0 });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await getData('push-campaign/list', { page: page + 1, limit, ...(debounced && { search: debounced }), ...(status && { status }) }, authToken(), false);
      setRows(res?.campaigns || []);
      setMeta(res?.meta || { total: 0 });
      setStats(res?.stats || {});
      setFirebase(res?.firebase || { ok: true });
      setError('');
    } catch {
      if (!quiet) setError('Could not load push notifications. Please try again.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [page, limit, debounced, status]);
  useEffect(() => { load(); }, [load]);

  // keep numbers moving while something is going out
  const live = rows.some((r) => ['running', 'pending'].includes(r.status));
  useEffect(() => {
    if (!live || view === 'form') return undefined;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [live, view, load]);

  const cancel = async () => {
    setBusy(true);
    try {
      await putData(`push-campaign/${cancelTarget._id}/cancel`, {}, authToken());
      setNotice(`“${cancelTarget.name}” was cancelled.`);
      setCancelTarget(null);
      load(true);
    } catch (e) {
      setError(e?.message || 'Could not cancel it.');
      setCancelTarget(null);
    } finally {
      setBusy(false);
    }
  };

  if (view === 'form') {
    return <PushForm source={source} onBack={() => { setView('list'); setSource(null); }} onCreated={(msg) => { setView('list'); setSource(null); setNotice(msg); setStatus(''); setPage(0); load(true); }} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Push notifications</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Message mobile app customers through Firebase.</Typography>
        </Box>
        <Button startIcon={<MdAdd />} variant="contained" onClick={() => { setSource(null); setView('form'); }} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: '#000', '&:hover': { background: 'rgb(235 190 0)' } }}>New notification</Button>
      </Box>

      {!firebase.ok && <Alert severity="error" sx={{ mb: 2 }}><b>Firebase is not connected.</b> {firebase.error} Notifications cannot be sent until this is fixed.</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, mb: 2.5 }}>
        <Kpi title="Campaigns" value={stats.campaigns} from="#6366f1" to="#4338ca" onClick={() => { setStatus(''); setPage(0); }} active={status === ''} />
        <Kpi title="Delivered to Firebase" value={stats.sent} from="#10b981" to="#047857" />
        <Kpi title="Failed" value={stats.failed} from="#ef4444" to="#b91c1c" onClick={() => { setStatus('failed'); setPage(0); }} active={status === 'failed'} />
        <Kpi title="Scheduled" value={stats.scheduled} from="#0ea5e9" to="#1d4ed8" onClick={() => { setStatus('scheduled'); setPage(0); }} active={status === 'scheduled'} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search name, title or ID…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f8fafc' }}>
                {['Campaign', 'Message', 'Audience', 'Result', 'When', 'Status', ''].map((h) => <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12, color: '#64748b' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7}>
                  <Box sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                    <MdNotificationsActive size={36} color="#cbd5e1" />
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, mt: 1 }}>{debounced || status ? 'Nothing matches those filters' : 'No push notifications yet'}</Typography>
                    {!debounced && !status && <Typography sx={{ fontFamily: FONT, fontSize: 13 }}>Press “New notification” to send your first one.</Typography>}
                  </Box>
                </TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell sx={{ fontFamily: FONT }}><b>{r.name}</b><Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{r.campId}</Typography></TableCell>
                  <TableCell sx={{ fontFamily: FONT, maxWidth: 280 }}><Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13 }}>{r.title}</Typography><Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{r.body}</Typography></TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12.5 }}>{audienceText(r.audience)}</TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#166534', fontWeight: 800 }}>{(r.stats?.sent || 0).toLocaleString('en-IN')}</span> sent
                    {r.stats?.failed > 0 && <> · <span style={{ color: '#dc2626', fontWeight: 800 }}>{r.stats.failed.toLocaleString('en-IN')}</span> failed</>}
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>of {(r.stats?.targeted || 0).toLocaleString('en-IN')} devices</Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtDateTime(r.scheduledAt || r.startedAt || r.createdAt)}</TableCell>
                  <TableCell><StatusChip status={r.status} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Details"><IconButton size="small" onClick={() => setSelected(r)} sx={{ color: '#4f46e5' }}><MdVisibility size={18} /></IconButton></Tooltip>
                    <Tooltip title="Send again as a new notification"><IconButton size="small" onClick={() => { setSource(r); setView('form'); }} sx={{ color: '#ca8a04' }}><MdContentCopy size={17} /></IconButton></Tooltip>
                    {r.status === 'scheduled' && <Tooltip title="Cancel"><IconButton size="small" onClick={() => setCancelTarget(r)} sx={{ color: '#dc2626' }}><MdCancel size={18} /></IconButton></Tooltip>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={meta.total || 0} page={page} rowsPerPage={limit} rowsPerPageOptions={[10, 20, 50]} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }} />
      </Paper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="lg" scroll="paper">
        {selected && (
          <>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}>{selected.name} <StatusChip status={selected.status} /></DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '1fr 300px' } }}>
                <Box>
                  {selected.status === 'failed' && selected.lastError && <Alert severity="error" sx={{ mb: 2 }} icon={<MdErrorOutline />}>{selected.lastError}</Alert>}
                  {[
                    ['Campaign ID', selected.campId],
                    ['Audience', audienceText(selected.audience)],
                    ['Devices found', (selected.stats?.targeted || 0).toLocaleString('en-IN')],
                    ['Sent to Firebase', (selected.stats?.sent || 0).toLocaleString('en-IN')],
                    ['Failed', (selected.stats?.failed || 0).toLocaleString('en-IN')],
                    ['Dead tokens switched off', (selected.stats?.removed || 0).toLocaleString('en-IN')],
                    ['Opens on tap', selected.link || '—'],
                    ['Scheduled for', selected.scheduledAt ? fmtDateTime(selected.scheduledAt) : 'Sent immediately'],
                    ['Started', fmtDateTime(selected.startedAt)],
                    ['Finished', fmtDateTime(selected.finishedAt)],
                    ['Created by', `${selected.createdByName || '—'} · ${fmtDateTime(selected.createdAt)}`],
                  ].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                  {(selected.failureReasons || []).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, mb: 0.75 }}>Why some did not go through</Typography>
                      {selected.failureReasons.map((e) => <Chip key={e.code} size="small" label={`${e.code.replace('messaging/', '')} · ${e.count}`} sx={{ mr: 0.75, mb: 0.75, background: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />)}
                    </Box>
                  )}
                </Box>
                <PushPreview title={selected.title} body={selected.body} imageUrl={selected.imageUrl} />
              </Box>
              <PushDelivery key={`${selected._id}-${selected.status}`} campaign={selected} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setSelected(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button></DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!cancelTarget} onClose={() => !busy && setCancelTarget(null)}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Cancel this notification?</DialogTitle>
        <DialogContent><Typography sx={{ fontFamily: FONT, fontSize: 14 }}>“{cancelTarget?.name}” is scheduled for {fmtDateTime(cancelTarget?.scheduledAt)}. It will not be sent.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}>Keep it</Button>
          <Button onClick={cancel} disabled={busy} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 800 }}>Cancel notification</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PushNotification;
